// Promise-basierter Ersatz für window.confirm() — rendert den App-eigenen
// ConfirmDialog statt des Browser-Popups ("Auf localhost:3000 wird …").
//
// Verwendung:
//   const { confirm, confirmDialog } = useConfirm();
//   ...
//   if (!(await confirm({ title, body, confirmLabel }))) return;
//   ...
//   return (<>{...}{confirmDialog}</>);
import React, { useCallback, useRef, useState } from "react";
import ConfirmDialog from "./ConfirmDialog";

export interface ConfirmOptions {
  title: string;
  body?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "default" | "danger";
}

export function useConfirm(): {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  confirmDialog: React.ReactElement;
  /** true, solange der Dialog offen ist — Aufrufer mit globalen Tastatur-
   *  Shortcuts MÜSSEN diese abschalten, sonst laufen sie hinter dem Dialog
   *  weiter (Max, 04.08.: Tippen im Regen-Hinweis-Feld akzeptierte Designs
   *  und blätterte den Desk weiter). */
  confirmOpen: boolean;
  /** Schließt einen offenen Dialog als "abgebrochen", z. B. wenn der Fall
   *  wechselt, auf den sich die Frage bezog. Ohne offenen Dialog wirkungslos. */
  dismiss: () => void;
} {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolver = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback((next: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      // Ein evtl. noch offener Dialog wird als "abgebrochen" aufgelöst.
      resolver.current?.(false);
      resolver.current = resolve;
      setOpts(next);
    });
  }, []);

  const close = useCallback((value: boolean) => {
    resolver.current?.(value);
    resolver.current = null;
    setOpts(null);
  }, []);
  const dismiss = useCallback(() => {
    if (resolver.current) close(false);
  }, [close]);

  const confirmDialog = (
    <ConfirmDialog
      open={Boolean(opts)}
      title={opts?.title}
      body={opts?.body}
      confirmLabel={opts?.confirmLabel}
      cancelLabel={opts?.cancelLabel}
      variant={opts?.variant || "default"}
      onConfirm={() => close(true)}
      onCancel={() => close(false)}
    />
  );

  return { confirm, confirmDialog, confirmOpen: Boolean(opts), dismiss };
}

export default useConfirm;
